import React, { Component } from "react";
import { Card, Button, Header, Image } from "semantic-ui-react";
import PropTypes from "prop-types";
import ApexCharts from "apexcharts";
import Constants from "../utilities/Constants";
import Strings from "../utilities/Strings";
import { connect } from "react-redux";
import { solveProblemAction } from "../../actions/changeActiveItemActions";

let h1WaveFormOptions = {
  series: [{ data: [] }],
  chart: {
    id: Constants.CHART_ID,
    type: "line",
  },
  colors: [Constants.RED_HEX],
  markers: {
    size: 0
  },
  dataLabels: {
    enabled: false
  },
  title: {
    text: Strings.WAVE_FORM,
    align: "center",
    style: {
      fontSize: "14px",
      fontFamily: "Helvetica, Arial, sans-serif"
    }
  },
  stroke: {
    curve: "straight",
    width: 2
  },
  xaxis: {
    type: "numeric",
    title: {
      text: Strings.TIME_US
    },
    crosshairs: {
      show: "true"
    },
    labels: {
      show: true,
      style: {
        fontSize: "14px",
        fontFamily: "Helvetica, Arial, sans-serif",
        cssClass: "apexcharts-xaxis-label",
      },
      formatter: function (val) {
        val = val / (10 ** 3);
        if (val !== undefined && val < 10) {
          return val.toFixed(2);
        }
        else if (val !== undefined && val >= 10) {
          return val.toFixed(0);
        }
        else {
          return val;
        }
      }
    }
  },
  yaxis: {
    min: -1000,
    max: 1000,
    labels: {
      show: true,
      formatter: function (val) {
        return val.toFixed(2) + " " + Strings.MILLIVOLT;
      },
      style: {
        colors: [],
        fontSize: "14px",
        fontFamily: "Helvetica, Arial, sans-serif",
        fontWeight: 400,
        cssClass: "apexcharts-yaxis-label",
      }
    },
    title: {
      text: Strings.VOLTAGE_MV
    },
  },
  annotations: {
    position: "front",
    xaxis: []
  }
};

export class ProblemCard extends Component {
  // Intialize this component state
  constructor(props) {
    super(props);
    this.state = {
      waveImg: ""
    };
  }

  getProblemHeader = () => {
    if (this.props.problem.type === Constants.H1_TERMINATION_OFF_PROBLEM_TYPE) {
      return (
        <Card.Header style={{ marginBottom: "2px" }}>
          {Strings.H1_TERMINATOR_OFF_LIST_HEADER}
        </Card.Header>
      );
    }
    else if (this.props.problem.type === Constants.H1_CAPACITIVE_EFFECT_PROBLEM_TYPE) {
      return (
        <Card.Header style={{ marginBottom: "2px" }}>
          {Strings.H1_CAPACITIVE_EFFECT_LIST_HEADER}
        </Card.Header>
      );
    }
    else if (this.props.problem.type === Constants.H1_HIGH_RESISTANCE_PROBLEM_TYPE) {
      return (
        <Card.Header style={{ marginBottom: "2px" }}>
          {Strings.H1_HIGH_RESISTANCE_LIST_HEADER}
        </Card.Header>
      );
    }
    else if (this.props.problem.type === Constants.H1_INVERTED_POLARITY_PROBLEM_TYPE) {
      return (
        <Card.Header style={{ marginBottom: "2px" }}>
          {Strings.H1_INVERTED_POLARITY_LIST_HEADER}
        </Card.Header>
      );
    }
    else if (this.props.problem.type === Constants.H1_NOISE_PROBLEM_TYPE) {
      return (
        <Card.Header style={{ marginBottom: "2px" }}>
          {Strings.H1_NOISE_LIST_HEADER}
        </Card.Header>
      );
    }
    else if (this.props.problem.type === Constants.H1_UNBALANCE_PROBLEM_TYPE) {
      return (
        <Card.Header style={{ marginBottom: "2px" }}>
          {Strings.H1_UNBALANCE_LIST_HEADER}
        </Card.Header>
      );
    }
    else {
      return "";
    }
  }

  formatTime = () => {
    if (this.props.problem.ntp !== null && this.props.problem.ntp === true) {
      let parsedMillis = Date.parse(this.props.problem.time);
      let dateToShow = new Date(parsedMillis);
      return dateToShow.toLocaleString("pt-BR");
    }
    else if (this.props.problem.time !== undefined && this.props.problem.time !== null) {
      const eventTimeInMillis = this.props.problem.time * 1000;
      const timeNow = this.props.nowMillis;
      const time = timeNow - eventTimeInMillis;
      const dateToShow = new Date(time);
      return (dateToShow.toLocaleString("pt-BR"));
    }
    else {
      return Strings.BEFORE_MONITOR_INIT_STR;
    }
  }

  getProblemMeta = () => {
    let problemDate = this.formatTime();

    return (
      <Card.Meta>
        <strong>
          {Strings.DETECTED_ON}
          {problemDate}
        </strong>
      </Card.Meta>
    );
  }

  getProblemDescription = () => {
    if (this.props.problem.type === Constants.H1_CAPACITIVE_EFFECT_PROBLEM_TYPE) {
      return (
        <Card.Description style={descriptionStyle}>
          <Header as="h4" style={headerStyle}>{Strings.DEVICE}:</Header>
          <p>
            <b>{Strings.CHANNEL}:</b> {this.props.channel}
          </p>
          <p>
            <b>{Strings.JITTER}:</b> {(this.props.problem.jitter).toFixed(2)} {Strings.MICROSSECOND}
          </p>
        </Card.Description>
      );
    }
    else if (this.props.problem.type === Constants.H1_HIGH_RESISTANCE_PROBLEM_TYPE) {
      return (
        <Card.Description style={descriptionStyle}>
          <Header as="h4" style={headerStyle}>{Strings.DEVICE}:</Header>
          <p>
            <b>{Strings.CHANNEL}:</b> {this.props.channel}
          </p>
          <p>
            <b>{Strings.AMPLITUDE}:</b> {(this.props.problem.amplitude).toFixed(2)} {Strings.MILLIVOLT}
          </p>
        </Card.Description>
      );
    }
    else if (this.props.problem.type === Constants.H1_INVERTED_POLARITY_PROBLEM_TYPE) {
      return (
        <Card.Description style={descriptionStyle}>
          <Header as="h4" style={headerStyle}>{Strings.DEVICE}:</Header>
          <p>
            <b>{Strings.ADDRESS}:</b> {this.props.problem.address}
          </p>
          <p>
            <b>{Strings.CHANNEL}:</b> {this.props.channel}
          </p>
        </Card.Description>
      );
    }
    else if (this.props.problem.type === Constants.H1_NOISE_PROBLEM_TYPE) {
      return (
        <Card.Description style={descriptionStyle}>
          <Header as="h4" style={headerStyle}>{Strings.DEVICE}:</Header>
          <p>
            <b>{Strings.CHANNEL}:</b> {this.props.channel}
          </p>
          <p>
            <b>{Strings.AMPLITUDE}:</b> {(this.props.problem.amplitudeIdle).toFixed(2)} {Strings.MILLIVOLT}
          </p>
        </Card.Description>
      );
    }
    else if (this.props.problem.type === Constants.H1_TERMINATION_OFF_PROBLEM_TYPE) {
      return (
        <Card.Description style={descriptionStyle}>
          <Header as="h4" style={headerStyle}>{Strings.DEVICE}:</Header>
          <p>
            <b>{Strings.CHANNEL}:</b> {this.props.channel}
          </p>
          <p>
            <b>{Strings.AMPLITUDE}:</b> {(this.props.problem.amplitude).toFixed(2)} {Strings.MILLIVOLT}
          </p>
        </Card.Description>
      );
    }
    else if (this.props.problem.type === Constants.H1_UNBALANCE_PROBLEM_TYPE) {
      return (
        <Card.Description style={descriptionStyle}>
          <Header as="h4" style={headerStyle}>{Strings.DEVICE}:</Header>
          <p>
            <b>{Strings.CHANNEL}:</b> {this.props.channel}
          </p>
          <p>
            <b>{Strings.UNBALANCE}:</b> {(this.props.problem.unbalance).toFixed(2) + " %"}
          </p>
        </Card.Description>
      );
    }
  }

  handleSolvedButtonClick = async () => {
    this.props.onProblemSolve(true, this.props.problem);
  }

  getFormatedH1Options = (h1WaveFormOptions) => {
    let options = {};

    // Set the time values to microsseconds
    options = {
      ...h1WaveFormOptions,
      xaxis: {
        type: "numeric",
        title: {
          text: Strings.TIME_US
        },
        crosshairs: {
          show: "true"
        }
      }
    };

    return options;
  }

  getOscilloscopeImgURI = async (wave) => {

    // Only try to generate the images if the array is not empty
    if (wave !== null && wave !== undefined) {
      document.getElementById("chart").style.display = "block";

      // Render the chart
      let chart = new ApexCharts(document.querySelector("#chart"), h1WaveFormOptions);
      chart.render();

      let options = this.getFormatedH1Options(h1WaveFormOptions);

      options = {
        ...options,
        title: {
          text: Strings.WAVE_FORM,
          align: "center",
          style: {
            fontSize: "14px",
            fontFamily: "Helvetica, Arial, sans-serif"
          }
        },
        colors: [Constants.RED_HEX],
        series: [
          { 
            name: Strings.WAVE_FORM, 
            data: wave
          }
        ]
      };

      chart.updateOptions(options, false, false, false);

      let imgURIObj = await chart.dataURI();

      document.getElementById("chart").style.display = "none";

      chart.destroy();

      return imgURIObj.imgURI;
    }
    else {
      return Constants.LOADING_IMG;
    }
  }

  setImages = async () => {
    let wave = "data:,";

    while (wave === "data:,") {
      wave = await this.getOscilloscopeImgURI(this.props.problem.wave);
    }

    this.setState({ waveImg: wave }, () => {
      let waveImgElement = document.getElementById(this.props.problem._id + " / imgWave");
      waveImgElement.style.visibility = "visible";
      waveImgElement.style.display = "block";
    });
  }


  componentDidMount = () => {
    this.setImages();
  }

  getEvidenceDescriptionText = () => {
    return <p>{Strings.EVIDENCE_DESCRIPTION}</p>;
  }

  render() {
    return (
      <Card id={this.props.problem._id} fluid raised>
        <Card.Content>

          {this.getProblemHeader()}
          {this.getProblemMeta()}
          {this.getProblemDescription()}

          <Card.Description>
            {this.getEvidenceDescriptionText()}
            <Image
              id={this.props.problem._id + " / imgWave"}
              centered
              src={this.state.waveImg}
              style={hiddenImageStyle}
              onError={() => console.log("Error loading image")}
            />
          </Card.Description>

        </Card.Content>
        <Card.Content extra>
          <Button
            basic
            color='green'
            fluid
            onClick={this.handleSolvedButtonClick}>
            {Strings.CHECKED}
          </Button>
        </Card.Content>
        <div id="chart" style={chartsStyle}></div>
      </Card >
    );
  }
}

const mapActionsToProps = {
  onProblemSolve: solveProblemAction
};

ProblemCard.propTypes = {
  nowMillis: PropTypes.number.isRequired,
  startTime: PropTypes.number.isRequired,
  problem: PropTypes.object.isRequired,
  onProblemSolve: PropTypes.func,
  channel: PropTypes.number.isRequired,
  amplitude: PropTypes.number,
  deviation: PropTypes.number,
};

const headerStyle = {
  marginTop: "8px",
  marginBottom: "8px",
};

const chartsStyle = {
  position: "absolute",
  left: "0px",
  top: "0px",
  zIndex: "-1",
  width: "525px",
  height: "250px",
  display: "none",
  visibility: "hidden"
};

const hiddenImageStyle = {
  visibility: "hidden",
  display: "none",
};

const descriptionStyle = {
  marginBottom: "1vh"
};

export default connect(null, mapActionsToProps)(ProblemCard);